import {
  CartaoIndicador,
  CelulaDeTotal,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  type ColunaTabela,
} from './ui';
import type { LinhaCalculo, ResultadoCalculadora } from '../servicos/calculadora';
import { formatarCompetencia } from '../servicos/formato';

type Totais = ResultadoCalculadora['totais'];

/**
 * O resultado de um cálculo de crédito — indicadores e tabela por competência.
 *
 * Mora aqui porque duas telas mostram o **mesmo** cálculo: a calculadora
 * pública, para o lead, e a simulação aberta pelo comercial. Se os rótulos
 * fossem escritos duas vezes, bastaria uma troca num lado para o comercial
 * ligar citando "economia" o que o lead viu como "valor a pagar".
 *
 * Semântica dos campos, conforme `CalculoCredito` (RN-005 a RN-010):
 * `valueSped` é o crédito utilizável, `valueBeneficio` é a **economia do
 * cliente** e `total` é o **valor a pagar**. Só o `valueBeneficio` leva o verde
 * de economia. Nenhum número é calculado aqui (RN-615): linhas e totais chegam
 * prontos do backend.
 */

export function IndicadoresDoCalculo(props: { readonly totais: Totais }): JSX.Element {
  const { totais } = props;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <CartaoIndicador
        rotulo="ICMS apurado"
        valor={<ValorFiscal valor={totais.totalSped} tamanho="grande" />}
      />
      <CartaoIndicador
        rotulo="Crédito utilizável"
        valor={<ValorFiscal valor={totais.valueSped} tamanho="grande" />}
      />
      <CartaoIndicador
        rotulo="Valor a pagar"
        valor={<ValorFiscal valor={totais.total} tamanho="grande" />}
        detalhe="Desembolso pelos créditos adquiridos."
      />
      <CartaoIndicador
        rotulo="Economia"
        valor={<ValorFiscal valor={totais.valueBeneficio} variante="economia" tamanho="grande" />}
        detalhe="Quanto deixa de ser pago com o deságio."
      />
    </div>
  );
}

const COLUNAS: readonly ColunaTabela<LinhaCalculo>[] = [
  {
    chave: 'competencia',
    titulo: 'Competência',
    larguraMinima: '8rem',
    celula: (linha) => formatarCompetencia(linha.competencia),
  },
  {
    chave: 'totalSped',
    titulo: 'ICMS apurado',
    numerica: true,
    celula: (linha) => <ValorFiscal valor={linha.totalSped} />,
  },
  {
    chave: 'percentSped',
    titulo: '% utilização',
    numerica: true,
    celula: (linha) => <ValorPercentual percentual={linha.percentSped} />,
  },
  {
    chave: 'valueSped',
    titulo: 'Crédito utilizável',
    numerica: true,
    celula: (linha) => <ValorFiscal valor={linha.valueSped} />,
  },
  {
    chave: 'percentBeneficio',
    titulo: '% deságio',
    numerica: true,
    celula: (linha) => <ValorPercentual percentual={linha.percentBeneficio} />,
  },
  {
    chave: 'total',
    titulo: 'Valor a pagar',
    numerica: true,
    celula: (linha) => <ValorFiscal valor={linha.total} />,
  },
  {
    chave: 'valueBeneficio',
    titulo: 'Economia',
    numerica: true,
    celula: (linha) => <ValorFiscal valor={linha.valueBeneficio} variante="economia" />,
  },
];

export function TabelaDoCalculo(props: {
  readonly linhas: readonly LinhaCalculo[];
  readonly totais: Totais;
  readonly legenda?: string;
}): JSX.Element {
  const { totais } = props;
  return (
    <Tabela
      legenda={props.legenda ?? 'Resultado do cálculo por competência'}
      densidade="compacta"
      colunas={COLUNAS}
      dados={props.linhas}
      chaveDaLinha={(linha) => linha.competencia}
      chaveDeCabecalhoDaLinha="competencia"
      rodape={
        // RN-615 — o rodapé usa `totais`, que veio pronto do backend.
        /*
          Sete células, uma por coluna: um rodapé com uma célula a mais que o
          cabeçalho desloca todos os totais para a direita, cada número sob o
          rótulo errado.
        */
        <tr>
          <CelulaDeTotal cabecalhoDaLinha>Total</CelulaDeTotal>
          <CelulaDeTotal numerica>
            <ValorFiscal valor={totais.totalSped} />
          </CelulaDeTotal>
          <CelulaDeTotal numerica>
            <span aria-hidden="true">—</span>
          </CelulaDeTotal>
          <CelulaDeTotal numerica>
            <ValorFiscal valor={totais.valueSped} />
          </CelulaDeTotal>
          <CelulaDeTotal numerica>
            <span aria-hidden="true">—</span>
          </CelulaDeTotal>
          <CelulaDeTotal numerica>
            <ValorFiscal valor={totais.total} />
          </CelulaDeTotal>
          <CelulaDeTotal numerica>
            <ValorFiscal valor={totais.valueBeneficio} variante="economia" />
          </CelulaDeTotal>
        </tr>
      }
    />
  );
}
